import type { ProjectContextValue, UserContextValue } from '@core/context';
import type { HubResponseProps } from '@readme/backend/iso';
import type { ProjectClientSide } from '@readme/backend/models/project/types';
import type { I18nLanguage } from '@readme/i18n';
import type { ReactNode } from 'react';

import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { ProjectContext, UserContext } from '@core/context';
import useEnvInfo from '@core/hooks/useEnvInfo';
import useUserPermissions from '@core/hooks/useUserPermissions';

import { AI_PANEL_LOCATION_STORAGE_KEY, AI_PANEL_STORAGE_KEY, useSuperHubStore } from '.';

export interface InitializeSuperHubStoreProps {
  children: ReactNode;
  currentBranch?: HubResponseProps['branch'];
  currentVersion: HubResponseProps['gitVersion'];
  language?: I18nLanguage;
}

const getSlimProjectObject = (project?: { _id; name; subdomain }) => ({
  id: project?._id,
  name: project?.name,
  subdomain: project?.subdomain,
});

/**
 * Middleware component connects to our App project and user contexts to then
 * initialize our SuperHub store and make it ready for this particular project.
 */
export function InitializeSuperHubStore({
  children,
  currentBranch,
  currentVersion,
  language,
}: InitializeSuperHubStoreProps) {
  const { isServer } = useEnvInfo();
  const [initialize, isReady, toggleAIPanel, setAIPanelLocation] = useSuperHubStore(s => [
    s.initialize,
    s.isReady,
    s.toggleAIPanel,
    s.setAIPanelLocation,
  ]);
  const { isAdminUser, isEditorUser, isEndUser, isGroupAdminUser, isOwner, isViewerUser } = useUserPermissions();
  const { project } = useContext(ProjectContext) as ProjectContextValue;
  const { isAuthenticated, isGod, notifications, user, permissions, stripeId } = useContext(
    UserContext,
  ) as unknown as UserContextValue;
  const location = useLocation();
  // To enable SSR, we must call this inline rather than inside an effect. On
  // the server, ignore the "ready" state b/c our global stores on the server
  // persist indefinitely and won't ever reset the "ready" state to false.
  if (isServer || !isReady) {
    const isEnterpriseGroup = !!project.childrenProjects?.length;
    const isEnterpriseChild = !!project.parent;
    const enterpriseParent = isEnterpriseGroup ? project : project.parent;
    const enterpriseProjects = (
      project.childrenProjects?.length ? project.childrenProjects : project.siblings
    ) as ProjectClientSide[];

    initialize({
      currentBranch: currentBranch || currentVersion,
      isEnterpriseGroup,
      isEnterpriseChild,
      enterpriseParent: getSlimProjectObject(enterpriseParent),
      enterpriseProjects: enterpriseProjects?.map(getSlimProjectObject),
      language: language || 'en',
      // Because hash route values are not available on the server, initialize
      // both SSR and the first client render without the location.hash to avoid
      // SSR/CSR mismatches.
      location: [location.pathname],
      projectFlags: project.flags,
      subdomain: project.subdomain,
      // Both default to the SSR-stable values. The post-mount effect below
      // upgrades them from localStorage so the server render and the first
      // client render produce identical DOM (no hydration mismatch). Reading
      // localStorage synchronously here would cause AIQuickInput in the sidebar
      // footer to flash on/off for users who chose `aiPanelLocation: 'right'`.
      isAIPanelOpen: false,
      aiPanelLocation: 'sidebar',
      user: {
        email: user?.email || null,
        id: user?.teammateUserId || null,
        isAuthenticated,
        isGod,
        isGroupAdmin: isGroupAdminUser,
        isGroupEndUser: isEndUser,
        isOwner,
        name: user?.name || null,
        notifications: notifications ?? null,
        role: isAdminUser ? 'admin' : isEditorUser ? 'editor' : isViewerUser ? 'viewer' : 'end-user',
        stripeId: stripeId || null,
        permissions: permissions?.map?.(p => p.project) || [],
      },
    });
  }

  // Upgrade `isAIPanelOpen` and `aiPanelLocation` from localStorage AFTER
  // hydration. Reading them synchronously during initialize would diverge from
  // the server render (which has no localStorage) and cause hydration warnings
  // — most visibly the AIQuickInput in the sidebar footer flashing on/off.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const storedOpen = window.localStorage.getItem(AI_PANEL_STORAGE_KEY);
    if (storedOpen === 'true') toggleAIPanel(true);
    const storedLocation = window.localStorage.getItem(AI_PANEL_LOCATION_STORAGE_KEY);
    if (storedLocation === 'right') setAIPanelLocation('right');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
}
